/**
 * NominateProviderDialog — form for members to add an independent provider.
 * Used by /trusted-providers page.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = [
  ['vet', 'Vet'],
  ['store', 'Store'],
  ['groomer', 'Groomer'],
  ['trainer', 'Trainer'],
  ['behaviorist', 'Behaviorist'],
  ['sitter', 'Pet sitter'],
  ['walker', 'Walker'],
  ['daycare', 'Daycare'],
  ['boarder', 'Boarder'],
  ['photographer', 'Photographer'],
  ['other', 'Other'],
];

const OWNERSHIPS = [
  ['independent', 'Independent / founder-owned'],
  ['family_owned', 'Family-owned'],
  ['employee_owned', 'Employee-owned (ESOP / worker co-op)'],
  ['cooperative', 'Cooperative (member-owned)'],
  ['nonprofit', 'Nonprofit'],
  ['academic', 'Academic / university'],
  ['unknown', "I don't know — please verify"],
];

export default function NominateProviderDialog({ open, onClose, onSubmitted }) {
  const [form, setForm] = useState({
    name: '', type: 'vet', ownership_type: 'independent',
    website: '', city: '', state: '', country: 'US',
    founded_year: '', service_notes: '',
  });
  const [busy, setBusy] = useState(false);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim() || !form.type) {
      toast.error('Name and type are required');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        state: form.state.trim().toUpperCase() || null,
        country: (form.country || 'US').toUpperCase(),
        founded_year: form.founded_year ? Number(form.founded_year) : null,
        website: form.website.trim() || null,
        city: form.city.trim() || null,
        service_notes: form.service_notes.trim() || null,
      };
      await axios.post(`${API}/independents/nominate`, payload);
      onSubmitted?.();
      setForm({ name: '', type: 'vet', ownership_type: 'independent', website: '', city: '', state: '', country: 'US', founded_year: '', service_notes: '' });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not submit nomination';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="nominate-dialog">
        <DialogHeader>
          <DialogTitle>Nominate a provider</DialogTitle>
          <DialogDescription>
            Tell us about a vet, store, trainer, sitter, or other pet pro that you think
            embodies service-over-profit. We'll verify ownership before publishing.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label htmlFor="np-name">Name *</Label>
            <Input id="np-name" value={form.name} onChange={(e) => setF('name', e.target.value)} placeholder="Mud Bay" data-testid="np-name" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Type *</Label>
              <Select value={form.type} onValueChange={(v) => setF('type', v)}>
                <SelectTrigger data-testid="np-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Ownership</Label>
              <Select value={form.ownership_type} onValueChange={(v) => setF('ownership_type', v)}>
                <SelectTrigger data-testid="np-ownership"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {OWNERSHIPS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="np-website">Website</Label>
            <Input id="np-website" value={form.website} onChange={(e) => setF('website', e.target.value)} placeholder="https://" data-testid="np-website" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <Label htmlFor="np-city">City</Label>
              <Input id="np-city" value={form.city} onChange={(e) => setF('city', e.target.value)} data-testid="np-city" />
            </div>
            <div>
              <Label htmlFor="np-state">State</Label>
              <Input id="np-state" value={form.state} maxLength={3} onChange={(e) => setF('state', e.target.value)} data-testid="np-state" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="np-country">Country</Label>
              <Input id="np-country" value={form.country} maxLength={2} onChange={(e) => setF('country', e.target.value)} data-testid="np-country" />
            </div>
            <div>
              <Label htmlFor="np-founded">Founded year</Label>
              <Input id="np-founded" type="number" value={form.founded_year} onChange={(e) => setF('founded_year', e.target.value)} placeholder="1988" data-testid="np-founded" />
            </div>
          </div>
          <div>
            <Label htmlFor="np-notes">Why they belong here</Label>
            <Textarea id="np-notes" rows={3} value={form.service_notes} onChange={(e) => setF('service_notes', e.target.value)} placeholder="Family-owned since…  Staff bench is deep on raw food…  Etc." data-testid="np-notes" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="np-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="np-submit">{busy ? 'Submitting…' : 'Submit nomination'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
